import type { ResolvedLeague } from "@/contexts";
import { usePrefetchOnHover, useScopedHref } from "@/hooks";
import { useT } from "@/i18n";
import { InputIcon, NavLink, NavLinkGroup, NavLinkGroupsList } from "@/ui";

import { useIsLeagueNavItemActive } from "./useIsLeagueNavItemActive";
import { getLeagueNavItemPath } from "./useLeagueNavGroups";
import { useVisibleLeagueNavGroups } from "./useVisibleLeagueNavGroups";

type LeagueGroupsListProps = {
  // Passed in (not read from context) so the nav keeps rendering while it
  // animates OUT after the league is cleared — see LeagueSidebarHeader.
  league: ResolvedLeague;
};

type LeagueNavItemLinkProps = {
  id: string;
  href: string;
  title: string;
  startIcon: InputIcon;
  isActive: boolean;
};

// Warms the target page chunk on hover/focus. The `@/ui` NavLink doesn't forward
// Aria hover events, so wrap it the same way the main sidebar NavLink does — the
// pointer/focus event bubbles up from the inner anchor to this `contents` div.
const LeagueNavItemLink = ({ id, href, title, startIcon, isActive }: LeagueNavItemLinkProps) => {
  const prefetch = usePrefetchOnHover(href);

  return (
    <div className="contents" onPointerEnter={prefetch} onFocus={prefetch}>
      <NavLink id={id} href={href} title={title} startIcon={startIcon} isActive={isActive} />
    </div>
  );
};

export const LeagueGroupsList = ({ league }: LeagueGroupsListProps) => {
  const t = useT();
  const scopedHref = useScopedHref();
  const leagueNavGroups = useVisibleLeagueNavGroups(league);
  const isActive = useIsLeagueNavItemActive(league.id);

  return (
    <NavLinkGroupsList
      allowsMultipleExpanded
      defaultExpandedKeys={leagueNavGroups.map((group) => group.type)}
    >
      {leagueNavGroups.map((group) => (
        <NavLinkGroup id={group.type} key={group.type} title={t(group.labelKey)} actions={null}>
          {group.items.map((item) => (
            <LeagueNavItemLink
              key={item.type}
              id={`league-nav-${item.type}`}
              href={scopedHref(`/league/${league.id}/${getLeagueNavItemPath(item)}`)}
              title={t(item.labelKey)}
              startIcon={item.icon}
              isActive={isActive(item)}
            />
          ))}
        </NavLinkGroup>
      ))}
    </NavLinkGroupsList>
  );
};
